import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Image,
  useColorMode,
  Button,
} from "@chakra-ui/react";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor, getBrandValue } from "../brandConfig";
import { useParams, useNavigate } from "react-router-dom";
import { useEffect } from "react";

const BACKGROUND_IMAGE_URL = "/Photos/Rock-4.jpg";

// Team member data (this could be moved to a separate data file)
const teamMembers = {
  "1": {
    name: "John Smith",
    title: "Chief Executive Officer",
    image: "/team/member1.jpg", // placeholder
    bio: "John has over 15 years of experience in investment management and strategic planning. He founded Providence Equity Holdings with a vision to create meaningful impact through responsible investing.",
    experience: [
      "15+ years in investment management",
      "Former Senior Portfolio Manager at Global Investments Inc.",
      "MBA in Finance from University of Queensland",
      "CFA Charterholder"
    ],
    specialties: ["Strategic Planning", "Portfolio Management", "Risk Assessment", "ESG Investing"]
  },
  "2": {
    name: "Sarah Johnson",
    title: "Chief Investment Officer",
    image: "/team/member2.jpg", // placeholder
    bio: "Sarah leads our investment strategy with a focus on sustainable and impactful investments. She brings extensive experience in alternative asset management and due diligence.",
    experience: [
      "12+ years in alternative investments",
      "Former Director at Australian Super Fund",
      "Master of Finance from Griffith University",
      "Specialist in ESG criteria evaluation"
    ],
    specialties: ["Alternative Investments", "Due Diligence", "ESG Analysis", "Portfolio Optimization"]
  },
  "3": {
    name: "Michael Chen",
    title: "Head of Research",
    image: "/team/member3.jpg", // placeholder
    bio: "Michael oversees our research initiatives and market analysis. His analytical expertise helps identify opportunities that align with our investment philosophy.",
    experience: [
      "10+ years in financial research",
      "Former Senior Analyst at Brisbane Investment Group",
      "PhD in Economics from University of Sydney",
      "Published researcher in sustainable finance"
    ],
    specialties: ["Market Research", "Economic Analysis", "Investment Modeling", "Sustainable Finance"]
  }
};

const TeamMemberDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { colorMode } = useColorMode();

  const member = teamMembers[id as keyof typeof teamMembers];

  // Theme-aware colors
  const bg = getColor("background.main", colorMode);

  // Preload background image
  useEffect(() => {
    const img = document.createElement('img');
    img.src = BACKGROUND_IMAGE_URL;
    img.onload = () => {
      console.log('Background image preloaded');
    };
  }, []);

  if (!member) {
    return (
      <Box
        bg={bg}
        minHeight="100vh"
        display="flex"
        flexDirection="column"
      >
        <NavbarWithCallToAction />
        <Container maxW="1200px" py={20} flex="1" display="flex" alignItems="center" justifyContent="center">
          <VStack spacing={4} textAlign="center">
            <Heading color="#FFFFFF" fontFamily={getBrandValue("fonts.heading")}>
              Team Member Not Found
            </Heading>
            <Button
              onClick={() => navigate("/about-us")}
              bg="#FF6600"
              color="#FFFFFF"
              _hover={{ bg: "#CC4902" }}
            >
              Back to About Us
            </Button>
          </VStack>
        </Container>
        <FooterWithFourColumns />
      </Box>
    );
  }

  return (
    <Box
      bg={bg}
      minHeight="100vh"
      position="relative"
      overflow="hidden"
      display="flex"
      flexDirection="column"
      sx={{
        overscrollBehaviorY: "none",
        WebkitOverflowScrolling: "touch",
        "@keyframes fadeInUp": {
          "0%": {
            opacity: 0,
            transform: "translateY(30px)"
          },
          "100%": {
            opacity: 1,
            transform: "translateY(0)"
          }
        }
      }}
    >
      <NavbarWithCallToAction />

      {/* Fixed Background Image Container with optimized loading */}
      <Box
        position="fixed"
        top="0"
        left="0"
        width="100%"
        height="100%"
        zIndex={0}
        backgroundImage={`url(${BACKGROUND_IMAGE_URL})`}
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundRepeat="no-repeat"
        backgroundColor="#1a1a1a"
        sx={{
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            zIndex: 1,
          }
        }}
      />

      {/* Scrolling Content Container */}
      <Box position="relative" zIndex={1} flex="1">
        {/* Back Button */}
        <Box pt={32} px={{ base: 6, md: 8 }}>
          <Container maxW="1200px">
            <Button
              leftIcon={<Text>←</Text>}
              onClick={() => {
                // Set flag to scroll to team section
                sessionStorage.setItem("scrollToTeamSection", "true");
                console.log("Set scrollToTeamSection flag"); // Debug log

                // Add subtle fade out effect for smoother transition
                const body = document.body;
                body.style.transition = 'opacity 0.1s ease-out';
                body.style.opacity = '0.95';

                // Navigate after brief fade
                setTimeout(() => {
                  console.log("Navigating to about-us"); // Debug log
                  navigate("/about-us");
                }, 50);
              }}
              bg="transparent"
              color="#FFFFFF"
              border="1px solid rgba(255, 255, 255, 0.3)"
              _hover={{
                bg: "rgba(255, 102, 0, 0.1)",
                borderColor: "#FF6600",
                transform: "translateY(-1px)"
              }}
              transition="all 0.2s ease"
              mb={8}
              size="lg"
              px={6}
            >
              Back to Team
            </Button>
          </Container>
        </Box>

        {/* Team Member Content */}
        <Box bg="transparent" position="relative" py={8} minHeight="80vh">
          <Container maxW="1400px" px={{ base: 6, md: 8 }}>
            {/* Two-column layout for desktop, stacked for mobile */}
            <HStack
              spacing={12}
              align="start"
              direction={{ base: "column", lg: "row" }}
              opacity={0}
              transform="translateY(30px)"
              animation="fadeInUp 1.0s ease-out 0.3s forwards"
            >
              {/* Left Column - Photo */}
              <VStack spacing={6} flex={{ base: "none", lg: "0 0 400px" }}>
                <Box
                  w={{ base: "280px", md: "350px", lg: "400px" }}
                  h={{ base: "350px", md: "440px", lg: "500px" }}
                  bg="rgba(226, 232, 240, 0.3)"
                  borderRadius="lg"
                  border="2px solid rgba(203, 213, 224, 0.5)"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  position="relative"
                  overflow="hidden"
                  backdropFilter="blur(10px)"
                >
                  <Text
                    color="#FFFFFF"
                    fontSize="lg"
                    fontWeight="500"
                    fontFamily={getBrandValue("fonts.body")}
                    textShadow="1px 1px 2px rgba(0,0,0,0.7)"
                    textAlign="center"
                  >
                    {member.name} Photo
                  </Text>
                </Box>
              </VStack>

              {/* Right Column - Content */}
              <VStack spacing={8} flex="1" align="start">
                {/* Name and Title */}
                <VStack spacing={3} align="start" w="100%">
                  <Heading
                    as="h1"
                    fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
                    fontWeight="700"
                    color="#FFFFFF"
                    fontFamily={getBrandValue("fonts.heading")}
                    textShadow="2px 2px 4px rgba(0,0,0,0.8)"
                  >
                    {member.name}
                  </Heading>
                  <Text
                    fontSize={{ base: "xl", md: "2xl" }}
                    color="#AAC3FF"
                    fontFamily={getBrandValue("fonts.body")}
                    fontWeight="500"
                    textShadow="1px 1px 3px rgba(0,0,0,0.8)"
                  >
                    {member.title}
                  </Text>
                </VStack>

                {/* Biography */}
                <Box
                  w="100%"
                  bg="rgba(255, 255, 255, 0.05)"
                  p={8}
                  borderRadius="lg"
                  border="1px solid rgba(255, 255, 255, 0.1)"
                  backdropFilter="blur(10px)"
                >
                  <VStack spacing={4} align="start">
                    <Heading
                      as="h3"
                      fontSize={{ base: "xl", md: "2xl" }}
                      fontWeight="600"
                      color="#FFFFFF"
                      fontFamily={getBrandValue("fonts.heading")}
                      textShadow="1px 1px 3px rgba(0,0,0,0.8)"
                    >
                      About
                    </Heading>
                    <Text
                      fontSize={{ base: "md", md: "lg" }}
                      color="rgba(255, 255, 255, 0.9)"
                      lineHeight="1.8"
                      fontFamily={getBrandValue("fonts.body")}
                      textShadow="1px 1px 2px rgba(0,0,0,0.7)"
                    >
                      {member.bio}
                    </Text>
                  </VStack>
                </Box>

                {/* Experience and Specialties - Stacked on mobile, side by side on desktop */}
                <VStack spacing={6} w="100%">
                  <HStack spacing={6} w="100%" align="start" direction={{ base: "column", md: "row" }}>
                    {/* Experience */}
                    <Box
                      flex="1"
                      bg="rgba(255, 255, 255, 0.05)"
                      p={6}
                      borderRadius="lg"
                      border="1px solid rgba(255, 255, 255, 0.1)"
                      backdropFilter="blur(10px)"
                      w="100%"
                    >
                      <VStack spacing={4} align="start">
                        <Heading
                          as="h4"
                          fontSize={{ base: "lg", md: "xl" }}
                          fontWeight="600"
                          color="#FFFFFF"
                          fontFamily={getBrandValue("fonts.heading")}
                          textShadow="1px 1px 3px rgba(0,0,0,0.8)"
                        >
                          Experience
                        </Heading>
                        <VStack spacing={2} align="start">
                          {member.experience.map((item, index) => (
                            <Text
                              key={index}
                              fontSize="sm"
                              color="rgba(255, 255, 255, 0.8)"
                              fontFamily={getBrandValue("fonts.body")}
                              textShadow="1px 1px 2px rgba(0,0,0,0.7)"
                            >
                              • {item}
                            </Text>
                          ))}
                        </VStack>
                      </VStack>
                    </Box>

                    {/* Specialties */}
                    <Box
                      flex="1"
                      bg="rgba(255, 255, 255, 0.05)"
                      p={6}
                      borderRadius="lg"
                      border="1px solid rgba(255, 255, 255, 0.1)"
                      backdropFilter="blur(10px)"
                      w="100%"
                    >
                      <VStack spacing={4} align="start">
                        <Heading
                          as="h4"
                          fontSize={{ base: "lg", md: "xl" }}
                          fontWeight="600"
                          color="#FFFFFF"
                          fontFamily={getBrandValue("fonts.heading")}
                          textShadow="1px 1px 3px rgba(0,0,0,0.8)"
                        >
                          Specialties
                        </Heading>
                        <VStack spacing={2} align="start">
                          {member.specialties.map((item, index) => (
                            <Text
                              key={index}
                              fontSize="sm"
                              color="rgba(255, 255, 255, 0.8)"
                              fontFamily={getBrandValue("fonts.body")}
                              textShadow="1px 1px 2px rgba(0,0,0,0.7)"
                            >
                              • {item}
                            </Text>
                          ))}
                        </VStack>
                      </VStack>
                    </Box>
                  </HStack>
                </VStack>
              </VStack>
            </HStack>
          </Container>
        </Box>

        {/* Extra space to ensure smooth scrolling */}
        <Box minHeight="20vh" />
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default TeamMemberDetails;